import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  TabList,
  Tabs,
  Typography,
} from '@mui/joy';
import {
  ArrowsClockwise as RefreshIcon,
  CheckCircle as ApproveIcon,
  MapPin as MapIcon,
  Ruler as MeasureIcon,
  MagnifyingGlass as SearchIcon,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import {
  apiErrorStatus,
  formatMoney,
  formatQuoteRange,
  mailCampaignsApi,
  mailMeasurementOrdersApi,
  mailRatesApi,
  mailerPinsApi,
  moneyToNumber,
} from '../../lib/directMailApi';
import { getApiErrorMessage } from '../../lib/apiError';
import type {
  MailerPin,
  MailerPinListParams,
  MailerPinStatus,
  MailerQueueStats,
} from '../../types/directMail';
import { MAILER_PIN_STATUS_LABELS } from '../../types/directMail';
import { DirectMailPageHeader, DIRECT_MAIL_ROUTES } from '../../components/directmail/DirectMailNav';
import { DirectMailStatTile } from '../../components/directmail/DirectMailStatTile';
import { MeasurePinDrawer } from '../../components/directmail/MeasurePinDrawer';
import { QueueTable } from '../../components/directmail/QueueTable';
import { SkipPinModal } from '../../components/directmail/SkipPinModal';
import { loadAllPages } from '../../components/directmail/loadAllPages';
import { useRoofQuoteWidget } from '../../components/directmail/roofQuotePricing';

type QueueTab = 'unclaimed' | 'mine' | 'measuring' | 'measured' | 'apexos';

// The pins endpoint splits `status` on commas. Repeating the query key instead
// would silently filter on the last value only.
const QUEUE_STATUS_FILTER = 'queued,measuring' as MailerPinStatus;

// DRF paginates at a fixed 20 rows: `page_size` is not a query param the API honours.
const PAGE_SIZE = 20;
const MEASUREMENT_RATE_KEY = 'measurement_service';

const TABS: { value: QueueTab; label: string; statKey: keyof MailerQueueStats }[] = [
  { value: 'unclaimed', label: 'Unclaimed', statKey: 'unclaimed' },
  { value: 'mine', label: 'Mine', statKey: 'mine' },
  { value: 'measuring', label: 'Measuring', statKey: 'measuring' },
  { value: 'measured', label: 'Measured', statKey: 'measured' },
  { value: 'apexos', label: 'ApexOS', statKey: 'apexos_pending' },
];

const EMPTY_COPY: Record<QueueTab, { title: string; body: string }> = {
  unclaimed: {
    title: 'Nothing waiting to be claimed',
    body: 'Pin houses on the Direct Mail map and queue them for measurement — they land here for your team to work.',
  },
  mine: {
    title: 'You have not claimed anything',
    body: 'Claim a pin from the Unclaimed tab. Claiming assigns it to you so nobody measures the same roof twice.',
  },
  measuring: {
    title: 'Nobody is mid-measurement',
    body: 'Pins show here between the moment someone claims them and the moment they save a roof size.',
  },
  measured: {
    title: 'No measured pins waiting on approval',
    body: 'Measure a claimed pin and its frozen price lands here for review. Approved pins are the pool you build a batch from.',
  },
  apexos: {
    title: 'ApexOS is not measuring anything for you',
    body: 'Select unclaimed pins and order measurement to have our team price them from satellite imagery.',
  },
};

interface SnackbarState {
  open: boolean;
  message: string;
  color: 'success' | 'danger' | 'warning' | 'neutral';
}

function paramsForTab(tab: QueueTab): MailerPinListParams {
  switch (tab) {
    case 'unclaimed':
      return { unclaimed: true };
    case 'mine':
      // queue-stats counts "mine" as queue work assigned to you, so the tab has
      // to say the same: without the status filter it would also list every pin
      // the user has already measured, batched or mailed.
      return { mine: true, status: QUEUE_STATUS_FILTER };
    case 'measuring':
      return { status: 'measuring' };
    case 'measured':
      return { status: 'measured' };
    case 'apexos':
      return { fulfillment: 'apexos', status: QUEUE_STATUS_FILTER };
  }
}

/**
 * Which tab lists a given pin, or null when no tab does (it has moved past the
 * queue). Mirrors `paramsForTab`, so a pin the map links to lands on a tab that
 * actually shows it.
 */
function tabForPin(pin: MailerPin, currentUserId?: number): QueueTab | null {
  if (pin.status === 'measured') return 'measured';
  if (pin.status !== 'queued' && pin.status !== 'measuring') return null;
  if (pin.fulfillment === 'apexos') return 'apexos';
  if (pin.assigned_to != null && pin.assigned_to === currentUserId) return 'mine';
  return pin.assigned_to == null ? 'unclaimed' : 'measuring';
}

export const DirectMailQueuePage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOrgAdmin = Boolean(user?.is_organization_admin || user?.is_superuser);

  const [tab, setTab] = useState<QueueTab>('unclaimed');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [busyPinId, setBusyPinId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    color: 'neutral',
  });

  const [worklist, setWorklist] = useState<MailerPin[]>([]);
  const [worklistIndex, setWorklistIndex] = useState<number | null>(null);
  const [skipPin, setSkipPin] = useState<MailerPin | null>(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [highlightedPinId, setHighlightedPinId] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [tab, search, campaignId]);

  const notify = (message: string, color: SnackbarState['color']) =>
    setSnackbar({ open: true, message, color });

  // The map links here with ?pin=<id>. The row can be on any tab and any page,
  // so the pin is looked up once and then used to drive the tab and the search
  // box — searching its address is what puts it on screen whatever the paging.
  const focusPinParam = searchParams.get('pin');
  useEffect(() => {
    const pinId = Number(focusPinParam);
    if (!focusPinParam || !Number.isFinite(pinId) || pinId <= 0) return;

    let cancelled = false;
    void (async () => {
      try {
        const pin = await mailerPinsApi.get(pinId);
        if (cancelled) return;
        const target = tabForPin(pin, user?.id);
        if (target === null) {
          notify(
            `${pin.formatted_address} is ${MAILER_PIN_STATUS_LABELS[pin.status].toLowerCase()} — it has left the measurement queue.`,
            'neutral',
          );
        } else {
          setTab(target);
          setSearchInput(pin.formatted_address);
          setHighlightedPinId(pin.id);
        }
      } catch (err) {
        if (!cancelled) notify(getApiErrorMessage(err, 'That pin could not be opened.'), 'danger');
      } finally {
        // Dropped once used, so switching tabs afterwards is not undone by it.
        if (!cancelled) {
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current);
              next.delete('pin');
              return next;
            },
            { replace: true },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [focusPinParam]);

  const listParams: MailerPinListParams = {
    ...paramsForTab(tab),
    search: search || undefined,
    campaign: campaignId ?? undefined,
    page,
  };

  const pinsQuery = useQuery({
    queryKey: ['direct-mail', 'pins', tab, search, campaignId, page],
    queryFn: () => mailerPinsApi.list(listParams),
  });

  const statsQuery = useQuery({
    queryKey: ['direct-mail', 'queue-stats'],
    queryFn: () => mailerPinsApi.queueStats(),
  });

  const campaignsQuery = useQuery({
    queryKey: ['direct-mail', 'campaigns', 'picker'],
    queryFn: () => loadAllPages((page_) => mailCampaignsApi.list({ page: page_ })),
    staleTime: 5 * 60 * 1000,
  });

  const ratesQuery = useQuery({
    queryKey: ['direct-mail', 'rates'],
    queryFn: () => mailRatesApi.list(),
  });

  const widgetQuery = useRoofQuoteWidget();

  const pins = pinsQuery.data?.results ?? [];
  const total = pinsQuery.data?.count ?? 0;
  const measurementRate = ratesQuery.data?.find((rate) => rate.key === MEASUREMENT_RATE_KEY);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'pins'] });
    void queryClient.invalidateQueries({ queryKey: ['direct-mail', 'queue-stats'] });
  };

  // Bulk selection only earns its clutter where there is an action behind it:
  // approving measured pins, or (for an admin) buying measurements.
  const selectable = tab === 'measured' || (tab === 'unclaimed' && isOrgAdmin);
  const selectedPins = useMemo(
    () => pins.filter((pin) => selectedIds.includes(pin.id)),
    [pins, selectedIds],
  );

  const toggleSelected = (id: number) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  const allSelected = pins.length > 0 && pins.every((pin) => selectedIds.includes(pin.id));

  const openMeasure = (pin: MailerPin, rows: MailerPin[]) => {
    const index = rows.findIndex((row) => row.id === pin.id);
    setWorklist(rows);
    setWorklistIndex(index >= 0 ? index : 0);
  };

  const closeMeasure = () => {
    setWorklistIndex(null);
    setWorklist([]);
  };

  const measurePin = worklistIndex === null ? null : (worklist[worklistIndex] ?? null);
  const hasNextInWorklist = worklistIndex !== null && worklistIndex + 1 < worklist.length;

  const claim = async (pin: MailerPin) => {
    setBusyPinId(pin.id);
    try {
      const claimed = await mailerPinsApi.claim(pin.id);
      refresh();
      // Claiming is only ever a prelude to measuring, so go straight there.
      openMeasure(claimed, pins.map((row) => (row.id === claimed.id ? claimed : row)));
    } catch (err) {
      const status = apiErrorStatus(err);
      notify(
        getApiErrorMessage(
          err,
          status === 409
            ? 'Someone on your team claimed that pin first.'
            : 'That pin could not be claimed.',
        ),
        status === 409 ? 'warning' : 'danger',
      );
      refresh();
    } finally {
      setBusyPinId(null);
    }
  };

  const release = async (pin: MailerPin) => {
    setBusyPinId(pin.id);
    try {
      await mailerPinsApi.release(pin.id);
      notify('Pin released back to the queue.', 'success');
      refresh();
    } catch (err) {
      notify(getApiErrorMessage(err, 'That pin could not be released.'), 'danger');
    } finally {
      setBusyPinId(null);
    }
  };

  const approveSelected = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const result = await mailerPinsApi.bulkApprove({ pin_ids: selectedIds });
      const rejected = result.rejected?.length ?? 0;
      notify(
        rejected > 0
          ? `${result.approved} approved. ${rejected} could not be: ${result.rejected[0].reason}`
          : `${result.approved} pin${result.approved === 1 ? '' : 's'} approved to mail.`,
        rejected > 0 ? 'warning' : 'success',
      );
      setSelectedIds([]);
      refresh();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Those pins could not be approved.'), 'danger');
    } finally {
      setBulkBusy(false);
    }
  };

  const orderMeasurements = async () => {
    if (selectedIds.length === 0) return;
    setBulkBusy(true);
    try {
      const order = await mailMeasurementOrdersApi.create({ pin_ids: selectedIds });
      notify(
        `Order ${order.reference} placed — ${order.pin_count} roofs for ${formatMoney(order.total_amount)}.`,
        'success',
      );
      setSelectedIds([]);
      setOrderModalOpen(false);
      refresh();
    } catch (err) {
      // A 402 carries the actionable "no card on file" text; show it verbatim.
      notify(getApiErrorMessage(err, 'That measurement order could not be placed.'), 'danger');
    } finally {
      setBulkBusy(false);
    }
  };

  const orderTotal = measurementRate
    ? moneyToNumber(measurementRate.price) * selectedIds.length
    : null;

  const emptyState = (
    <Sheet variant="soft" sx={{ p: 4, borderRadius: 'sm', textAlign: 'center' }}>
      <Typography level="title-md">{EMPTY_COPY[tab].title}</Typography>
      <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 1, mb: 2 }}>
        {EMPTY_COPY[tab].body}
      </Typography>
      {(tab === 'unclaimed' || tab === 'apexos') && (
        <Button
          component={RouterLink}
          to={DIRECT_MAIL_ROUTES.map}
          variant="soft"
          startDecorator={<MapIcon />}
        >
          Go to the pin map
        </Button>
      )}
    </Sheet>
  );

  return (
    <Box>
      <DirectMailPageHeader
        title="Measurement queue"
        description="Claim a pinned house, measure the roof, and approve the price that gets printed."
        actions={
          <Button
            variant="outlined"
            color="neutral"
            startDecorator={<RefreshIcon />}
            onClick={refresh}
            loading={pinsQuery.isFetching && !pinsQuery.isLoading}
          >
            Refresh
          </Button>
        }
      />

      {widgetQuery.data && widgetQuery.data.tiers.length === 0 && (
        <Alert color="warning" variant="soft" sx={{ mb: 2 }}>
          <Box>
            <Typography level="title-sm">Roof Quote pricing is not set up</Typography>
            <Typography level="body-sm">
              Measuring a pin cannot produce a price until your Roof Quote Widget has a tier with a
              price per square.
            </Typography>
            <Button
              component={RouterLink}
              to="/marketing/roof-quote-widget"
              size="sm"
              variant="plain"
              sx={{ px: 0, mt: 0.5 }}
            >
              Set up Roof Quote pricing
            </Button>
          </Box>
        </Alert>
      )}

      <Grid container spacing={1} sx={{ mb: 2 }}>
        {TABS.map((entry) => (
          <Grid key={entry.value} xs={6} sm={4} md={2.4}>
            <DirectMailStatTile
              label={entry.label}
              value={statsQuery.data?.[entry.statKey] ?? 0}
              loading={statsQuery.isLoading}
              selected={tab === entry.value}
              onClick={() => setTab(entry.value)}
            />
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_, value) => setTab(value as QueueTab)} sx={{ bgcolor: 'transparent' }}>
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
          {TABS.map((entry) => (
            <Tab key={entry.value} value={entry.value}>
              {entry.label}
            </Tab>
          ))}
        </TabList>
      </Tabs>

      <Card variant="outlined" sx={{ mt: 2, p: 2, gap: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'flex-end' }}>
          <FormControl sx={{ flex: 1 }}>
            <FormLabel>Search</FormLabel>
            <Input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              startDecorator={<SearchIcon />}
              placeholder="Address, owner or ZIP"
            />
          </FormControl>
          <FormControl sx={{ minWidth: { md: 220 } }}>
            <FormLabel>Campaign</FormLabel>
            <Select
              value={campaignId}
              onChange={(_, value) => setCampaignId(value)}
              slotProps={{ button: { 'aria-label': 'Filter by campaign' } }}
            >
              <Option value={null}>All campaigns</Option>
              {(campaignsQuery.data ?? []).map((campaign) => (
                <Option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </Option>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {selectable && pins.length > 0 && (
          <>
            <Divider />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() =>
                    setSelectedIds(allSelected ? [] : pins.map((pin) => pin.id))
                  }
                >
                  {allSelected ? 'Clear selection' : `Select all ${pins.length} on this page`}
                </Button>
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                  {selectedIds.length} selected
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1}>
                {tab === 'measured' && (
                  <Button
                    size="sm"
                    startDecorator={<ApproveIcon />}
                    disabled={selectedIds.length === 0}
                    loading={bulkBusy}
                    onClick={() => void approveSelected()}
                  >
                    Approve {selectedIds.length || ''}
                  </Button>
                )}
                {tab === 'unclaimed' && isOrgAdmin && (
                  <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={<MeasureIcon />}
                    disabled={selectedIds.length === 0 || !measurementRate}
                    onClick={() => setOrderModalOpen(true)}
                  >
                    Have ApexOS measure {selectedIds.length || ''}
                  </Button>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {pinsQuery.isLoading ? (
          <Stack spacing={1}>
            {[0, 1, 2, 3, 4].map((row) => (
              <Skeleton key={row} variant="rectangular" height={52} sx={{ borderRadius: 'sm' }} />
            ))}
          </Stack>
        ) : pinsQuery.isError ? (
          <Alert color="danger" variant="soft">
            <Box>
              <Typography level="title-sm">The queue could not be loaded</Typography>
              <Typography level="body-sm">
                {getApiErrorMessage(pinsQuery.error, 'Please try again in a moment.')}
              </Typography>
              <Button size="sm" variant="plain" sx={{ px: 0, mt: 0.5 }} onClick={() => void pinsQuery.refetch()}>
                Retry
              </Button>
            </Box>
          </Alert>
        ) : (
          <QueueTable
            pins={pins}
            selectedIds={selectedIds}
            onToggleSelected={toggleSelected}
            selectable={selectable}
            currentUserId={user?.id}
            isOrgAdmin={isOrgAdmin}
            busyPinId={busyPinId}
            highlightedPinId={highlightedPinId}
            onClaim={(pin) => void claim(pin)}
            onRelease={(pin) => void release(pin)}
            onMeasure={(pin) => openMeasure(pin, pins)}
            onSkip={(pin) => setSkipPin(pin)}
            emptyState={emptyState}
          />
        )}

        {total > PAGE_SIZE && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </Typography>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={page === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              disabled={page * PAGE_SIZE >= total}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </Stack>
        )}
      </Card>

      <MeasurePinDrawer
        open={measurePin !== null}
        pin={measurePin}
        widget={widgetQuery.data}
        campaignTierId={campaignsQuery.data?.find((campaign) => campaign.id === measurePin?.campaign)?.quote_tier}
        hasNext={hasNextInWorklist}
        onClose={closeMeasure}
        onSaved={(saved, advance) => {
          notify(
            `${saved.formatted_address} priced at ${formatQuoteRange(saved.quote_price_low, saved.quote_price_high)}.`,
            'success',
          );
          refresh();
          setWorklist((current) => current.map((row) => (row.id === saved.id ? saved : row)));
          if (advance && hasNextInWorklist) {
            setWorklistIndex((current) => (current === null ? null : current + 1));
          }
        }}
        onSkip={(pin) => {
          closeMeasure();
          setSkipPin(pin);
        }}
      />

      <SkipPinModal
        open={skipPin !== null}
        pin={skipPin}
        onClose={() => setSkipPin(null)}
        onSkipped={(pin) => {
          setSkipPin(null);
          notify(`${pin.formatted_address} skipped.`, 'success');
          refresh();
        }}
      />

      <Modal open={orderModalOpen} onClose={() => setOrderModalOpen(false)}>
        <ModalDialog sx={{ width: { xs: '92vw', sm: 460 } }}>
          <DialogTitle>Have ApexOS measure these roofs</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ pt: 1 }}>
              <Typography level="body-sm">
                Our team measures each roof from satellite imagery and prices it from your own Roof
                Quote tiers. The pins come back measured and ready to approve.
              </Typography>
              <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Roofs</Typography>
                  <Typography level="body-sm">{selectedIds.length}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Price per roof</Typography>
                  <Typography level="body-sm">
                    {measurementRate ? formatMoney(measurementRate.price) : '—'}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="title-sm">Charged to your card on file</Typography>
                  <Typography level="title-sm">
                    {orderTotal === null ? '—' : formatMoney(orderTotal)}
                  </Typography>
                </Stack>
              </Sheet>
              {selectedPins.some((pin) => pin.status !== 'pinned' && pin.status !== 'queued') && (
                <Alert color="warning" variant="soft">
                  Some selected pins are already past the queue. The order is rejected unless every
                  pin is still waiting to be measured.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button loading={bulkBusy} onClick={() => void orderMeasurements()}>
              Place order
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setOrderModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      <Snackbar
        open={snackbar.open}
        color={snackbar.color}
        variant="soft"
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};
